// ─────────────────────────────────────────────────────────────────────────────
// RECORDING A LICENCE CHECK, ON THE PARTNER'S OWN RECORD.
//
// 🚨 THE RECORDING ROUTES SHIPPED WITH NO UI. POST /partners/nmls-verification
// and POST /partners/state-license-verification have existed since 1 Oct and
// nothing called either — so the manual verification a payout now depends on was
// reachable only by someone writing a curl command. This is the entry point, and
// it sits on the partner record because that is where a rep already is when the
// question comes up.
//
// 🔑 THE LOOKUP LINK IS THE SERVER'S, NOT TYPED HERE. A staff member pasting a
// licence number into a search box is where transcription errors come from, and a
// verification recorded against a mistyped number is a signed statement about
// somebody else's licence.
//
// ⚠️ IT OFFERS NO FORM WHERE THE SERVER WOULD REFUSE ONE. On the automated route
// — Texas, which publishes an open dataset — a manual reading would sit beside an
// authoritative answer with nothing saying which governs, and the route returns
// 409 AUTOMATED_STATE. The panel renders the TREC answer and a re-check instead.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 🔑 THE STATE WORDS ARE THE SERVER'S. adminNmlsState decides invalid / missing /
// unverified / verified / stale; this only colours them, so a new state added
// there renders in grey rather than disappearing.
const TONE = {
  verified: { bg: '#f0fdf4', border: '#bbf7d0', fg: '#166534' },
  stale: { bg: '#fffbeb', border: '#fde68a', fg: '#92400e' },
  unverified: { bg: '#f9fafb', border: '#e5e7eb', fg: '#6b7280' },
  missing: { bg: '#f9fafb', border: '#e5e7eb', fg: '#6b7280' },
  invalid: { bg: '#fef2f2', border: '#fecaca', fg: '#991b1b' },
};

/** The one input style this panel uses, so the review form matches the rest. */
const FIELD = { width: '100%', padding: '7px 9px', fontSize: 12.5, borderRadius: 7,
  border: '1px solid #d1d5db', boxSizing: 'border-box', fontFamily: 'inherit' };

export default function LicenseVerificationPanel({ partnerId, token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [openFor, setOpenFor] = useState(null);
  const [form, setForm] = useState({ status: '', state: '', note: '', documentId: '' });
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState('');
  // 🔑 A SEPARATE FORM, because it answers a different question from "what did
  // the register say". The register already said it; this records what Doorly
  // decided about an order.
  const [review, setReview] = useState({ open: false, decision: '', note: '', reference: '' });
  // 🚨 THE DOCUMENTS THE PARTNER SENT. Until today they could not send one, so
  // this list is empty everywhere — see the partner-facing card. Accepting one
  // is what lifts a hold the register cannot lift itself.
  const [evidence, setEvidence] = useState([]);
  const [evNote, setEvNote] = useState({});
  // 🚨 THE NAME QUESTION. An automated register deliberately offers no "record a
  // check" form — a human's reading beside an authoritative dataset leaves two
  // answers. That is right for a STATUS and wrong for a NAME: no amount of
  // re-querying TREC says whether "Toni Cramond" and "TONIKA CANETTE
  // GREENWOOD-CRAMOND" are one person. So the automated route had a finding only
  // a human could resolve and nowhere to resolve it.
  const [idNote, setIdNote] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    if (!partnerId || !token) return;
    fetch(`${API}/api/admin/partners/${partnerId}/license-verification`, { headers })
      .then((r) => r.json())
      .then((d) => { if (d.error) setError(d.error); else { setData(d); setError(''); } })
      .catch(() => setError('Could not load the licence check.'));
    // ⚠️ ITS OWN REQUEST, GUARDED SEPARATELY. The submissions are a different
    // table from the verification columns, and a failure to read them must cost
    // the list rather than the panel.
    fetch(`${API}/api/admin/partners/${partnerId}/license-evidence`, { headers })
      .then((r) => r.json())
      .then((d) => setEvidence(d.evidence || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, token]);

  useEffect(load, [load]);

  const record = async (subj) => {
    setBusy(true); setRefusal('');
    try {
      const isNmls = data.registry === 'nmls';
      const url = isNmls
        ? `${API}/api/admin/partners/nmls-verification`
        : `${API}/api/admin/partners/state-license-verification`;
      const body = isNmls
        ? { subject: subj.subject, id: subj.id, status_observed: form.status,
          note: form.note, evidence_document_id: form.documentId || undefined }
        : { partner_id: subj.id, status_observed: form.status, state_observed: form.state,
          note: form.note, evidence_document_id: form.documentId || undefined };
      const res = await fetch(url, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      // ⚠️ THE SERVER'S SENTENCE, NOT A GENERIC FAILURE. "Record the status
      // exactly as the register shows it" and "that number is not a valid NMLS
      // ID, request a corrected one first" are different problems with different
      // answers, and both are actionable.
      if (!res.ok) { setRefusal(d.error || 'The check could not be recorded.'); return; }
      // 🔑 AND IT SAYS WHETHER THE READING ACTUALLY CLEARED ANYTHING. A green
      // tick beside a recorded "Terminated" is worse than no tick.
      if (d.permits_payout === false) {
        setRefusal(`Recorded — but "${d.status_observed}" does not release the fee. `
          + 'The hold stands and only the licensee and their regulator can change it.');
      }
      setOpenFor(null);
      setForm({ status: '', state: '', note: '', documentId: '' });
      load();
    } catch (_) {
      setRefusal('Could not reach the server. Nothing was recorded.');
    } finally { setBusy(false); }
  };

  const recordIdentity = async (decision) => {
    setBusy(true); setRefusal('');
    try {
      const res = await fetch(`${API}/api/admin/partners/license-identity-review`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ partner_id: partnerId, decision, note: idNote }),
      });
      const d = await res.json();
      if (!res.ok) { setRefusal(d.error || 'That could not be recorded.'); return; }
      // ⚠️ "NOT THE SAME PERSON" IS THE DECISION THAT SENDS A DOCUMENT REQUEST.
      // A reviewer should know they have just sent one.
      if (d.asks_for_document) {
        setRefusal('Recorded. They are now being asked to upload a copy of their license — '
          + 'until now they were being asked for nothing.');
      }
      setIdNote('');
      load();
    } catch (_) {
      setRefusal('Could not reach the server. Nothing was recorded.');
    } finally { setBusy(false); }
  };

  const reviewEvidence = async (id, result) => {
    setBusy(true); setRefusal('');
    try {
      const res = await fetch(`${API}/api/admin/partners/license-evidence/${id}/review`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ result, note: evNote[id] || '' }),
      });
      const d = await res.json();
      if (!res.ok) { setRefusal(d.error || 'That could not be recorded.'); return; }
      // 🔑 "ACCEPTED" AND "THE HOLD IS LIFTED" ARE NOT THE SAME SENTENCE. A
      // document accepted against a finding the register has since replaced
      // leaves the partner exactly where they were, and a reviewer who is not
      // told that will think they have finished.
      if (result === 'accepted' && !d.hold_lifted) {
        setRefusal('Recorded — but the hold stands. The register now reads '
          + `"${d.outcome}", which is not the finding this document was sent to answer.`);
      }
      load();
    } catch (_) {
      setRefusal('Could not reach the server. Nothing was recorded.');
    } finally { setBusy(false); }
  };

  const recordReview = async () => {
    setBusy(true); setRefusal('');
    try {
      const res = await fetch(`${API}/api/admin/partners/license-disciplinary-review`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ partner_id: partnerId, decision: review.decision,
          note: review.note, order_reference: review.reference || undefined }),
      });
      const d = await res.json();
      if (!res.ok) { setRefusal(d.error || 'The decision could not be recorded.'); return; }
      // ⚠️ "do not permit" IS RECORDED SUCCESSFULLY AND STILL BLOCKS. Saying so
      // is the difference between a reviewer thinking they cleared somebody and
      // knowing they did not.
      if (!d.verified) {
        setRefusal('Recorded. This partner stays unverified — nothing is said to them about '
          + 'the order, and they are not asked for any document.');
      }
      setReview({ open: false, decision: '', note: '', reference: '' });
      load();
    } catch (_) {
      setRefusal('Could not reach the server. Nothing was recorded.');
    } finally { setBusy(false); }
  };

  if (error) {
    return <div style={{ fontSize: 12.5, color: '#b91c1c', padding: '10px 0' }}>{error}</div>;
  }
  if (!data) return null;
  if (!data.registry) {
    // 🔑 SAID PLAINLY. An empty panel reads as a loading failure.
    return (
      <div style={{ fontSize: 12.5, color: '#6b7280', padding: '10px 0' }}>
        This partner type carries no licence registry, so there is nothing to verify.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#111827' }}>
          {/* 🚨 "License", NOT "Licence". Jesse named this label, and DoorlyDesk
              is American English throughout — the British spelling survived here
              because the file's own comments use it. */}
          License verification
        </h4>
        <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{data.lookup}</span>
      </div>
      {/* 🚨 WHETHER THE PARTNER IS ALREADY BEING ASKED FOR A DOCUMENT. Without
          this a rep records a check without realising the partner is chasing a
          photocopy on the strength of the finding being replaced. */}
      {data.asks_for_document && (
        <div style={{ fontSize: 12.5, color: '#92400e', background: '#fffbeb',
          border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', margin: '8px 0 12px',
          lineHeight: 1.5 }}>
          This partner is currently being asked to upload their licence — reason shown to them:
          <strong> {data.reason_shown}</strong>. Recording a check here is what clears it.
        </div>
      )}

      {/* ── 🚨 DISCIPLINED, AND STILL PERMITTED TO PRACTISE ──────────────
          The register publishes statuses that mean "under a regulator's order
          AND may still work": TREC's "Probated Suspension - Active" and its two
          siblings, DBPR's "Probation,Active" and "Suspended,Active". Neither a
          pass nor a negative is honest about that, so a person reads the order.
          The partner sees nothing about any of this. */}
      {data.disciplinary && (
        <div style={{ fontSize: 12.5, color: '#3730a3', background: '#eef2ff',
          border: '1px solid #c7d2fe', borderRadius: 8, padding: '10px 12px',
          margin: '8px 0 12px', lineHeight: 1.5 }}>
          <div style={{ fontWeight: 800, marginBottom: 3 }}>
            Staff review needed — license status &ldquo;{data.disciplinary.status_observed}&rdquo;
          </div>
          <div>{data.disciplinary.copy}</div>
          {data.disciplinary.superseded_decision && (
            <div style={{ marginTop: 6, color: '#92400e' }}>
              A previous decision (<strong>{data.disciplinary.superseded_decision.replace(/_/g, ' ')}</strong>)
              was recorded against a different status and no longer applies.
            </div>
          )}
          {data.disciplinary.decided && !data.disciplinary.superseded_decision && (
            <div style={{ marginTop: 6 }}>
              Recorded: <strong>{data.disciplinary.decision.replace(/_/g, ' ')}</strong>.
            </div>
          )}
          {!review.open && (
            <button type="button" onClick={() => setReview({ ...review, open: true })}
              style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: '#3730a3',
                background: '#fff', border: '1px solid #c7d2fe', borderRadius: 7,
                padding: '5px 11px', cursor: 'pointer' }}>
              {data.disciplinary.decided ? 'Record a new decision' : 'Record a decision'}
            </button>
          )}
          {review.open && (
            <div style={{ marginTop: 9, display: 'grid', gap: 7 }}>
              <select value={review.decision}
                onChange={(e) => setReview({ ...review, decision: e.target.value })}
                style={FIELD}>
                <option value="">Select a decision…</option>
                <option value="permit">Permit — they may work with Doorly under this order</option>
                <option value="do_not_permit">Do not permit</option>
              </select>
              <input value={review.reference} placeholder="Order reference (optional)"
                onChange={(e) => setReview({ ...review, reference: e.target.value })}
                style={FIELD} />
              {/* 🚨 THE NOTE IS REQUIRED SERVER-SIDE. A bare "permit" is an
                  unattributable judgement about a regulator's order. */}
              <textarea value={review.note} rows={3}
                placeholder="What does the order say, and why does it permit (or not permit) this work? Required."
                onChange={(e) => setReview({ ...review, note: e.target.value })}
                style={{ ...FIELD, resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" disabled={busy || !review.decision || !review.note.trim()}
                  onClick={recordReview}
                  style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#4338ca',
                    border: 0, borderRadius: 7, padding: '6px 13px',
                    cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                  {busy ? 'Recording…' : 'Record decision'}
                </button>
                <button type="button"
                  onClick={() => setReview({ open: false, decision: '', note: '', reference: '' })}
                  style={{ fontSize: 12, color: '#6b7280', background: 'transparent',
                    border: 0, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── 🚨 IS THIS THE SAME PERSON? ──────────────────────────────────
          The register resolved the number and spells the holder differently.
          Nothing the partner can send answers that better than somebody reading
          the two names, and the register is one click away. */}
      {data.identity && (
        <div style={{ fontSize: 12.5, color: '#3730a3', background: '#eef2ff',
          border: '1px solid #c7d2fe', borderRadius: 8, padding: '10px 12px',
          margin: '8px 0 12px', lineHeight: 1.5 }}>
          <div style={{ fontWeight: 800, marginBottom: 4 }}>
            The name on the license is spelled differently
          </div>
          {/* 🔑 BOTH NAMES, SIDE BY SIDE. That IS the decision — putting them
              anywhere but next to each other is what made this a document
              request instead of a two-second judgement. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px',
            margin: '6px 0 8px', fontSize: 12.5 }}>
            <span style={{ color: '#6b7280' }}>On the register</span>
            <strong>{data.identity.registry_name || '—'}</strong>
            <span style={{ color: '#6b7280' }}>On their profile</span>
            <strong>{data.identity.profile_name || '—'}</strong>
          </div>
          <div>{data.identity.copy}</div>
          {data.identity.superseded_decision && (
            <div style={{ marginTop: 6, color: '#92400e' }}>
              A previous decision (<strong>{data.identity.superseded_decision.replace(/_/g, ' ')}</strong>)
              was recorded against a different name on the register and no longer applies.
            </div>
          )}
          {data.identity.decided && !data.identity.superseded_decision ? (
            <div style={{ marginTop: 6 }}>
              Recorded: <strong>{data.identity.decision.replace(/_/g, ' ')}</strong>.
            </div>
          ) : (
            <div style={{ marginTop: 9, display: 'grid', gap: 7 }}>
              <input value={idNote}
                placeholder="Note — required only to say it is NOT them"
                onChange={(e) => setIdNote(e.target.value)} style={FIELD} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" disabled={busy}
                  onClick={() => recordIdentity('same_person')}
                  style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#166534',
                    border: 0, borderRadius: 7, padding: '6px 13px', cursor: 'pointer' }}>
                  Same person
                </button>
                <button type="button" disabled={busy || !idNote.trim()}
                  onClick={() => recordIdentity('not_the_same_person')}
                  style={{ fontSize: 12, fontWeight: 700,
                    color: idNote.trim() ? '#991b1b' : '#9ca3af', background: '#fff',
                    border: `1px solid ${idNote.trim() ? '#fecaca' : '#e5e7eb'}`,
                    borderRadius: 7, padding: '6px 13px',
                    cursor: idNote.trim() ? 'pointer' : 'not-allowed' }}>
                  Not the same person
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── 🚨 WHAT THE PARTNER SENT, AND THE JUDGEMENT THAT ENDS IT ──────
          The register will go on saying what it says; a PDF does not change
          TREC. What lifts the hold is a person deciding this document answers
          the specific finding and putting their name to it. */}
      {evidence.length > 0 && (
        <div style={{ margin: '8px 0 14px' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#111827', marginBottom: 6 }}>
            License documents sent by this partner
          </div>
          {evidence.map((e) => (
            <div key={e.id} style={{ border: '1px solid #e5e7eb', background: '#fff',
              borderRadius: 9, padding: '10px 12px', marginBottom: 7 }}>
              <div style={{ fontSize: 12.5, color: '#111827', fontWeight: 700 }}>
                {e.file_name || `Submission #${e.id}`}
                <span style={{ fontWeight: 500, color: '#6b7280' }}>
                  {' '}· {new Date(e.submitted_at).toLocaleDateString()}
                </span>
              </div>
              {/* 🔑 THE QUESTION IT WAS SENT TO ANSWER, in the words the partner
                  was shown. A reviewer judging it against a different question
                  is the one way this goes wrong. */}
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                Sent to answer: <strong>{e.answers}</strong>
              </div>
              <a href={`${API}${e.download}`} style={{ fontSize: 12, color: '#3a6ad9' }}
                target="_blank" rel="noreferrer">Download it</a>
              {e.review_result ? (
                <div style={{ fontSize: 12, marginTop: 6,
                  color: e.review_result === 'accepted' ? '#166534' : '#991b1b' }}>
                  <strong>{e.review_result}</strong>
                  {e.review_note ? ` — ${e.review_note}` : ''}
                </div>
              ) : (
                <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                  {/* ⚠️ THE NOTE IS REQUIRED ON A REJECTION, not on an
                      acceptance. They are asked again after a rejection, and a
                      second ask with no reason is the one they write in about. */}
                  <input value={evNote[e.id] || ''}
                    placeholder="Why it does not answer the finding (required to reject)"
                    onChange={(ev) => setEvNote((p) => ({ ...p, [e.id]: ev.target.value }))}
                    style={FIELD} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" disabled={busy}
                      onClick={() => reviewEvidence(e.id, 'accepted')}
                      style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#166534',
                        border: 0, borderRadius: 7, padding: '6px 13px', cursor: 'pointer' }}>
                      It answers the finding
                    </button>
                    <button type="button" disabled={busy || !(evNote[e.id] || '').trim()}
                      onClick={() => reviewEvidence(e.id, 'rejected')}
                      style={{ fontSize: 12, fontWeight: 700,
                        color: (evNote[e.id] || '').trim() ? '#991b1b' : '#9ca3af',
                        background: '#fff',
                        border: `1px solid ${(evNote[e.id] || '').trim() ? '#fecaca' : '#e5e7eb'}`,
                        borderRadius: 7, padding: '6px 13px',
                        cursor: (evNote[e.id] || '').trim() ? 'pointer' : 'not-allowed' }}>
                      It does not
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {data.subjects.map((s) => {
        const tone = TONE[s.state] || TONE.unverified;
        const canRecord = data.registry === 'nmls' ? true : s.manual_allowed !== false;
        return (
          <div key={`${s.subject}-${s.id}`} style={{ border: `1px solid ${tone.border}`,
            background: tone.bg, borderRadius: 10, padding: '11px 13px', marginBottom: 9 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#111827' }}>{s.title}</div>
            <div style={{ fontSize: 12.5, color: tone.fg, marginTop: 3 }}>
              {s.number || 'no number on record'}
              {s.label ? ` · ${s.label}` : ''}
              {s.status_observed ? ` · read as "${s.status_observed}"` : ''}
              {s.verified_at ? ` · ${new Date(s.verified_at).toLocaleDateString()}` : ''}
            </div>
            {/* ⚠️ THE AUTOMATED ROUTE SHOWS ITS ANSWER AND OFFERS NO FORM. */}
            {s.trec && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                TREC: {s.trec.status || '—'}
                {s.trec.note ? ` · ${s.trec.note}` : ''}
                {s.trec.full_name ? ` · ${s.trec.full_name}` : ''}
                {s.trec.checked_at ? ` · checked ${new Date(s.trec.checked_at).toLocaleDateString()}` : ''}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8,
              flexWrap: 'wrap' }}>
              {s.lookup_url && (
                <a href={s.lookup_url} target="_blank" rel="noreferrer"
                  style={{ fontSize: 12, fontWeight: 700, color: '#3a6ad9' }}>
                  Open {s.lookup_name} ↗
                </a>
              )}
              {canRecord ? (
                <button type="button" onClick={() => setOpenFor(openFor === s.subject ? null : s.subject)}
                  style={{ border: 'none', background: '#3a6ad9', color: '#fff', borderRadius: 7,
                    padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  {openFor === s.subject ? 'Cancel' : 'Record a check'}
                </button>
              ) : (
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  Verified automatically — re-check rather than record
                </span>
              )}
            </div>

            {openFor === s.subject && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${tone.border}` }}>
                {/* 🔑 THE EXPECTED WORDS ARE OFFERED, because the register's own
                    spelling is what gets stored and a free-text box invites a
                    paraphrase. "Approved-Inactive" and "Approved" differ by one
                    word and by whether the person may originate. */}
                <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700,
                  color: '#374151', marginBottom: 4 }}>
                  Status, exactly as the register shows it
                </label>
                <input list={`statuses-${s.subject}`} value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  placeholder={(data.expected_statuses || [])[0] || ''}
                  style={{ width: '100%', padding: '7px 9px', fontSize: 12.5, borderRadius: 7,
                    border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
                <datalist id={`statuses-${s.subject}`}>
                  {(data.expected_statuses || []).map((v) => <option key={v} value={v} />)}
                </datalist>
                {data.registry === 'state_real_estate' && (
                  <>
                    <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700,
                      color: '#374151', margin: '8px 0 4px' }}>
                      Which state register did you read? (two letters)
                    </label>
                    <input value={form.state} maxLength={2}
                      onChange={(e) => setForm((f) => ({ ...f, state: e.target.value.toUpperCase() }))}
                      placeholder={s.state_observed
                        || (s.title.match(/—\s*([A-Z]{2})$/) || [])[1] || ''}
                      style={{ width: 70, padding: '7px 9px', fontSize: 12.5, borderRadius: 7,
                        border: '1px solid #d1d5db' }} />
                  </>
                )}
                <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700,
                  color: '#374151', margin: '8px 0 4px' }}>
                  Saved printout — document id (optional)
                </label>
                <input value={form.documentId}
                  onChange={(e) => setForm((f) => ({ ...f, documentId: e.target.value }))}
                  placeholder="e.g. 4312"
                  style={{ width: 120, padding: '7px 9px', fontSize: 12.5, borderRadius: 7,
                    border: '1px solid #d1d5db' }} />
                <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700,
                  color: '#374151', margin: '8px 0 4px' }}>Note (optional)</label>
                <input value={form.note}
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                  style={{ width: '100%', padding: '7px 9px', fontSize: 12.5, borderRadius: 7,
                    border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
                <button type="button" onClick={() => record(s)}
                  disabled={busy || !form.status.trim()
                    || (data.registry === 'state_real_estate' && form.state.trim().length !== 2)}
                  style={{ marginTop: 10, border: 'none', background: '#1d6b45', color: '#fff',
                    borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700,
                    cursor: busy ? 'wait' : 'pointer',
                    opacity: form.status.trim() ? 1 : 0.5 }}>
                  {busy ? 'Recording…' : 'Record this check'}
                </button>
                <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
                  Valid for {data.valid_for_days} days. A check with no status is a timestamp
                  saying somebody opened a page, so the status is required.
                </div>
              </div>
            )}
          </div>
        );
      })}
      {refusal && (
        <div style={{ fontSize: 12.5, color: '#991b1b', background: '#fef2f2',
          border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', lineHeight: 1.5 }}>
          {refusal}
        </div>
      )}
    </div>
  );
}
